import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
} from 'components/StdList';
import Immutable from 'immutable';
import React, { CSSProperties, useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { SubmittalOrderedMap } from 'records/Submittal';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';
import ApprovedSubmittalFolderRenderer from './ApprovedSubmittalFolderRenderer';
import NoApprovedSubmittalFolders from './NoApprovedSubmittalFolders';

interface Props extends WithAppStoreProps {
  selectedSpecNumber: string;
  approvedSubmittalGroups: Immutable.OrderedMap<string, SubmittalOrderedMap>;
  onSelectSpecNumber: (nextSpecNumber: string) => void;
}

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: Immutable.OrderedMap<string, SubmittalOrderedMap>;
}

interface StyleProps {
  height: number;
  width: number;
}

const ApprovedSubmittalFolders = (props: Props) => {
  const [docsLoaded, setDocsLoaded] = useState<boolean>(false);
  const { translate } = useTranslator(['specSections']);
  useEffect(() => {
    init();
  }, []);

  const init = async (): Promise<void> => {
    const { approvedSubmittalGroups, appState, actions } = props;
    const { selectedProject } = appState;

    // const project = projects.get(selectedProject, )

    const docIds = approvedSubmittalGroups
      .valueSeq()
      .flatMap((sg) => sg.valueSeq().map((a) => a.attachments))
      .flatten()
      .toList();

    if (docIds.size > 0) {
      await actions.getDocuments(selectedProject, docIds);
    }

    // When there is no docs, this instance of calling setState is called
    // Super early, basically still in the constructor. In order to make
    // sure the component has finished loading/mounting, we wait for a frame
    // before calling setState.
    window.setTimeout(() => {
      setDocsLoaded(true);
    }, 16);
  };

  const { selectedSpecNumber, approvedSubmittalGroups, onSelectSpecNumber } =
    props;

  const itemHeight = 64;

  return (
    <StdList gridTemplateColumns="1fr" id="approved-sub-folders-list">
      <StdListHeader>
        <StdListHeaderCell
          text={translate('submittalsPage:approvedFolders')}
          leftBorder={false}
        />
      </StdListHeader>
      <StdListBody enableScrolling={false}>
        <AutoSizer>
          {({ height, width }: StyleProps) => (
            <FixedSizeList
              height={height}
              width={width}
              itemCount={approvedSubmittalGroups.size}
              itemSize={itemHeight}
              itemData={approvedSubmittalGroups}
            >
              {({ style, index, data }: RenderingProps) =>
                docsLoaded &&
                approvedSubmittalGroups.size > 0 &&
                ApprovedSubmittalFolderRenderer(
                  style,
                  index,
                  data,
                  selectedSpecNumber,
                  onSelectSpecNumber
                )
              }
            </FixedSizeList>
          )}
        </AutoSizer>
        {docsLoaded && approvedSubmittalGroups.size === 0 ? (
          <NoApprovedSubmittalFolders />
        ) : null}
      </StdListBody>
    </StdList>
  );
};

export default withAppStore<Props>(ApprovedSubmittalFolders);
